/**
 * Veri ızgarası hücre aralığı seçimi: saf mantık (DOM/React yok). Konumlar sıfır tabanlı ve
 * *görünen* satır/sütun dizinidir (satır numarası sütunu sayılmaz). `DataTable` yalnız bu
 * modülün sonuçlarını çizer ve panoya yazar (docs/DATA-GRID-PLAN.md Faz 2).
 */

export interface CellPos {
  row: number
  col: number
}

/** `anchor`: seçimin başladığı hücre (sabit); `focus`: Shift+ok/sürükleme ile hareket eden uç. */
export interface CellRange {
  anchor: CellPos
  focus: CellPos
}

export interface GridBounds {
  rows: number
  cols: number
}

/** Köşeleri sıralanmış (üst ≤ alt, sol ≤ sağ) aralık; ikisi de dahil. */
export interface NormalizedRange {
  top: number
  bottom: number
  left: number
  right: number
}

export const singleCell = (pos: CellPos): CellRange => ({ anchor: pos, focus: pos })

export function normalizeRange(range: CellRange): NormalizedRange {
  return {
    top: Math.min(range.anchor.row, range.focus.row),
    bottom: Math.max(range.anchor.row, range.focus.row),
    left: Math.min(range.anchor.col, range.focus.col),
    right: Math.max(range.anchor.col, range.focus.col),
  }
}

export function rangeSize(range: CellRange): { rows: number; cols: number; cells: number } {
  const { top, bottom, left, right } = normalizeRange(range)
  const rows = bottom - top + 1
  const cols = right - left + 1
  return { rows, cols, cells: rows * cols }
}

/** "Seçim": birden çok hücrelik aralık. Tek hücre yalnız imleçtir (satıra tıklama bastırılmaz). */
export const isMultiCell = (range: CellRange | null | undefined): range is CellRange =>
  range != null && rangeSize(range).cells > 1

export function containsCell(range: CellRange, pos: CellPos): boolean {
  const { top, bottom, left, right } = normalizeRange(range)
  return pos.row >= top && pos.row <= bottom && pos.col >= left && pos.col <= right
}

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), Math.max(max - 1, 0))

const clampPos = (pos: CellPos, bounds: GridBounds): CellPos => ({
  row: clamp(pos.row, bounds.rows),
  col: clamp(pos.col, bounds.cols),
})

/** Veri küçülünce (sayfa/filtre) aralığı sınırlara çeker; hücre kalmadıysa `null`. */
export function clampRange(range: CellRange | null, bounds: GridBounds): CellRange | null {
  if (!range || bounds.rows === 0 || bounds.cols === 0) return null
  return { anchor: clampPos(range.anchor, bounds), focus: clampPos(range.focus, bounds) }
}

/** Ctrl+A: bütün hücreler; odak son hücrede, bağlantı ilkinde. */
export function selectAll(bounds: GridBounds): CellRange | null {
  if (bounds.rows === 0 || bounds.cols === 0) return null
  return {
    anchor: { row: 0, col: 0 },
    focus: { row: bounds.rows - 1, col: bounds.cols - 1 },
  }
}

export interface NavigationKey {
  key: string
  shift?: boolean
  /** Ctrl ya da Cmd. */
  ctrl?: boolean
}

/**
 * Klavye hareketi. Ok: odağı bir hücre taşır (Shift yoksa seçim tek hücreye iner, Shift ile bağlantı
 * sabit kalıp aralık genişler). Ctrl+ok: o yönün ucuna. Home/End: satırın başı/sonu (Ctrl ile ızgaranın
 * köşesi). PageUp/PageDown: `pageRows` satır. Tanınmayan tuşta `null` (olay işlenmez).
 */
export function moveSelection(
  range: CellRange,
  { key, shift = false, ctrl = false }: NavigationKey,
  bounds: GridBounds,
  pageRows = 10,
): CellRange | null {
  if (bounds.rows === 0 || bounds.cols === 0) return null
  const lastRow = bounds.rows - 1
  const lastCol = bounds.cols - 1
  const { row, col } = range.focus
  let next: CellPos
  switch (key) {
    case 'ArrowUp':
      next = { row: ctrl ? 0 : row - 1, col }
      break
    case 'ArrowDown':
      next = { row: ctrl ? lastRow : row + 1, col }
      break
    case 'ArrowLeft':
      next = { row, col: ctrl ? 0 : col - 1 }
      break
    case 'ArrowRight':
      next = { row, col: ctrl ? lastCol : col + 1 }
      break
    case 'Home':
      next = { row: ctrl ? 0 : row, col: 0 }
      break
    case 'End':
      next = { row: ctrl ? lastRow : row, col: lastCol }
      break
    case 'PageUp':
      next = { row: row - pageRows, col }
      break
    case 'PageDown':
      next = { row: row + pageRows, col }
      break
    default:
      return null
  }
  const focus = clampPos(next, bounds)
  return shift ? { anchor: range.anchor, focus } : singleCell(focus)
}

/** Fare: tıklama yeni aralığı başlatır; Shift+tıklama (ya da sürükleme) bağlantıyı koruyup odağı taşır. */
export function extendTo(range: CellRange, pos: CellPos): CellRange {
  return { anchor: range.anchor, focus: pos }
}

/** Panoya/özete giren hücre değeri: `number` ham sayıdır, biçimlenmez. */
export type GridCellValue = string | number | null | undefined

/** Seçili hücrelerin değerleri, satır satır (`values(row, col)` görünen dizinle çağrılır). */
export function collectValues(
  range: CellRange,
  values: (row: number, col: number) => GridCellValue,
): GridCellValue[][] {
  const { top, bottom, left, right } = normalizeRange(range)
  const matrix: GridCellValue[][] = []
  for (let row = top; row <= bottom; row++) {
    const line: GridCellValue[] = []
    for (let col = left; col <= right; col++) line.push(values(row, col))
    matrix.push(line)
  }
  return matrix
}

/**
 * Pano ondalık ayırıcısı. Uygulama Türkçe; Excel (tr-TR) `1234,5`'i sayı okur, `1234.5`'i metin
 * bırakır. Binlik ayırıcı yoktur ("biçimsiz"): Excel hücreyi kendi biçimiyle gösterir.
 */
export const CLIPBOARD_DECIMAL_SEPARATOR = ','

function clipboardNumber(value: number, decimal: string): string {
  if (!Number.isFinite(value)) return ''
  let text = String(value)
  // 1e-7 / 1e21 gibi üstel gösterim Excel'de sayı olmaz; düz yazıya çevir.
  if (/e/i.test(text)) {
    text = value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
  }
  return decimal === '.' ? text : text.replace('.', decimal)
}

/** Sekme/satır sonu/çift tırnak içeren metin Excel'in kuralıyla tırnaklanır. */
function clipboardText(value: string): string {
  return /[\t\r\n"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value
}

/**
 * Excel/Sheets uyumlu TSV: sütunlar sekmeyle, satırlar `\n` ile (sondaki satır sonu yok). Sayılar
 * biçimsiz (binliksiz), `null`/`undefined` boş hücre.
 */
export function buildTsv(
  matrix: readonly (readonly GridCellValue[])[],
  decimal: string = CLIPBOARD_DECIMAL_SEPARATOR,
): string {
  return matrix
    .map((line) =>
      line
        .map((value) => {
          if (value === null || value === undefined) return ''
          return typeof value === 'number' ? clipboardNumber(value, decimal) : clipboardText(value)
        })
        .join('\t'),
    )
    .join('\n')
}

/**
 * Seçimin sayısal toplamı: yalnız `number` değerler (biçimli sayı sütunları ham sayı verir); metin
 * toplanmaz. Kayan nokta artığı (0,1+0,2) 12 anlamlı haneye yuvarlanır. Sayısal hücre yoksa `count: 0`.
 */
export function sumNumeric(matrix: readonly (readonly GridCellValue[])[]): {
  sum: number
  count: number
} {
  let sum = 0
  let count = 0
  for (const line of matrix) {
    for (const value of line) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        sum += value
        count += 1
      }
    }
  }
  return { sum: Number(sum.toPrecision(12)), count }
}

/**
 * Sürüklerken otomatik kaydırma adımı (px, işaretli): imleç `lo`..`hi` aralığının kenarına `edge` kadar
 * yaklaştıkça artar (kenarda ya da dışında `maxStep`), ortada 0. Negatif = yukarı/sola.
 */
export function edgeScrollDelta(
  pos: number,
  lo: number,
  hi: number,
  edge: number,
  maxStep: number,
): number {
  if (hi - lo <= edge * 2) return 0
  if (pos < lo + edge) return -Math.round(Math.min((lo + edge - pos) / edge, 1) * maxStep)
  if (pos > hi - edge) return Math.round(Math.min((pos - (hi - edge)) / edge, 1) * maxStep)
  return 0
}
